"use client"

import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Building2, Search, Loader2, UserCheck } from "lucide-react"
import { fetchDepartments, addDepartment, editDepartment, deleteDepartment } from "../store/slices/departmentSlice"
import { Eye, Edit, Trash2, Plus, ChevronLeft, ChevronRight } from "lucide-react"

const PRIMARY_COLOR = "blue-600"

const AddDepartmentForm = ({ onClose }) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    head_of_department: "",
    address: "",
    type: "ITI",
  })
  const [error, setError] = useState(null)
  const { loading } = useSelector((state) => state.departments || {})

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await dispatch(addDepartment(formData)).unwrap()
      setFormData({ name: "", code: "", head_of_department: "", address: "", type: "ITI" })
      onClose()
    } catch (err) {
      setError(err.message || "Failed to add Institute")
    }
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Add New Institute</h2>
      {error && (
        <div className="bg-red-50 border border-red-200 p-3 rounded-lg mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Institute Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Institute Code</label>
          <input
            type="text"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Head of Institute</label>
          <input
            type="text"
            value={formData.head_of_department}
            onChange={(e) => setFormData({ ...formData, head_of_department: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Address</label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter institute address"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Institute Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="ITI">ITI</option>
            <option value="POLY">Polytechnic</option>
          </select>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 bg-${PRIMARY_COLOR} text-white rounded-lg hover:bg-blue-700 flex items-center ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {loading && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
            Add Institute
          </button>
        </div>
      </form>
    </div>
  )
}

const DepartmentPopup = ({ mode, department, onClose, onSuccess }) => {
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    name: department?.name || "",
    code: department?.code || "",
    head_of_department: department?.head_of_department || "",
    address: department?.address || "",
    type: department?.type || "ITI",
  })
  const [error, setError] = useState(null)
  const { loading } = useSelector((state) => state.departments || {})

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (mode === "add") {
        await dispatch(addDepartment(formData)).unwrap()
        await dispatch(fetchDepartments())
        onSuccess && onSuccess("Institute added successfully")
      } else if (mode === "edit") {
        const payload = {
          name: formData.name,
          code: formData.code,
          head_of_department: formData.head_of_department,
          address: formData.address || null,
          type: formData.type,
        }
        await dispatch(editDepartment({ id: department.id || department.department_id, data: payload })).unwrap()
        await dispatch(fetchDepartments())
        onSuccess && onSuccess("Institute updated successfully")
      } else if (mode === "delete") {
        await dispatch(deleteDepartment(department.id || department.department_id)).unwrap()
        await dispatch(fetchDepartments())
        onSuccess && onSuccess("Institute deleted successfully")
      }
      onClose()
    } catch (err) {
      setError(err.message || `Failed to ${mode} Institute`)
    }
  }

  if (mode === "view") {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Institute Details</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Institute ID</label>
            <p className="mt-1 text-gray-900">{department.department_id}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Institute Name</label>
            <p className="mt-1 text-gray-900">{department.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Institute Code</label>
            <p className="mt-1 text-gray-900">{department.code || "N/A"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Head of Institute</label>
            <p className="mt-1 text-gray-900">{department.head_of_department || "N/A"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Address</label>
            <p className="mt-1 text-gray-900">{department.address || "N/A"}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Institute Type</label>
            <p className="mt-1 text-gray-900">{department.type || "N/A"}</p>
          </div>
          <div className="flex justify-end">
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (mode === "delete") {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Confirm Delete</h2>
        <p className="text-gray-700 mb-4">
          Are you sure you want to delete the Institute <strong>{department.name}</strong>?
        </p>
        {error && (
          <div className="bg-red-50 border border-red-200 p-3 rounded-lg mb-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}
        <div className="flex justify-end space-x-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {loading && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
            Delete
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">{mode === "add" ? "Add New Institute" : "Edit Institute"}</h2>
      {error && (
        <div className="bg-red-50 border border-red-200 p-3 rounded-lg mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Institute Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Institute Code</label>
          <input
            type="text"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Head of Institute</label>
          <input
            type="text"
            value={formData.head_of_department}
            onChange={(e) => setFormData({ ...formData, head_of_department: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Address</label>
          <input
            type="text"
            value={formData.address || ""}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter institute address"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Institute Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="mt-1 w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="ITI">ITI</option>
            <option value="POLY">Polytechnic</option>
          </select>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 bg-${PRIMARY_COLOR} text-white rounded-lg hover:bg-blue-700 flex items-center ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {loading && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
            {mode === "add" ? "Add Institute" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  )
}

const DepartmentComponent = () => {
  const dispatch = useDispatch()
  const { departments = [], loading = false, error = null } = useSelector((state) => state.departments || {})
  const [searchTerm, setSearchTerm] = useState("")
  const [popup, setPopup] = useState({ isOpen: false, mode: "", department: null })
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [success, setSuccess] = useState({ show: false, text: "" })

  useEffect(() => {
    dispatch(fetchDepartments())
  }, [dispatch])

  const handleSuccess = (text) => {
    setSuccess({ show: true, text })
    setTimeout(() => setSuccess({ show: false, text: "" }), 3000)
  }

  const filteredDepartments = departments.filter((dept) => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return dept.name.toLowerCase().includes(term) || dept.code.toLowerCase().includes(term)
  })

  const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedDepartments = filteredDepartments.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value))
    setCurrentPage(1)
  }

  const openPopup = (mode, department = null) => {
    setPopup({ isOpen: true, mode, department })
  }

  const closePopup = () => {
    setPopup({ isOpen: false, mode: "", department: null })
  }

  if (loading && !popup.isOpen) {
    return (
      <div className={`flex items-center justify-center py-12 text-${PRIMARY_COLOR}`}>
        <Loader2 className="animate-spin h-8 w-8" />
        <span className="ml-2">Loading...</span>
      </div>
    )
  }

  if (error && !popup.isOpen) {
    return (
      <div className="bg-red-50 border border-red-200 p-4 rounded-xl shadow-sm">
        <p className="text-red-700 font-medium">Error: {error.message || error}</p>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div className="flex items-center gap-3">
          <Building2 className={`w-8 h-8 text-${PRIMARY_COLOR}`} />
          <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight">Institute Management</h2>
        </div>
        <button
          onClick={() => openPopup("add")}
          className={`px-4 py-2 bg-${PRIMARY_COLOR} text-white rounded-lg hover:bg-blue-700 flex items-center`}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Institute
        </button>
      </div>

      {/* Success banner */}
      {success.show && (
        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-800">
          {success.text}
        </div>
      )}

      {/* Search Box */}
      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-6">
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by institute name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
          />
        </div>
      </div>

      {/* Departments Table */}
      {paginatedDepartments.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-lg text-center border-2 border-dashed border-gray-300">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-600">No Institute Found</p>
          <p className="text-gray-500 mt-1">Try adjusting your search criteria.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Institute Id
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Institute Code
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Head of Institute
                  </th>
                  
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Type
                  </th>
                
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedDepartments.map((dept) => (
                  <tr key={dept.department_id} className="hover:bg-blue-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {dept.department_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{dept.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{dept.code || "N/A"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {dept.head_of_department || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{dept.type || "N/A"}</td>
               
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openPopup("view", dept)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors group"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        </button>
                        <button
                          onClick={() => openPopup("edit", dept)}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-full transition-colors group"
                          title="Edit Institute"
                        >
                          <Edit className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        </button>
                        <button
                          onClick={() => openPopup("delete", dept)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-full transition-colors group"
                          title="Delete Institute"
                        >
                          <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200">
            <div className="flex items-center space-x-2 mb-4 sm:mb-0">
              <span className="text-sm text-gray-600">Show</span>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : `bg-${PRIMARY_COLOR} text-white hover:bg-blue-700`
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      currentPage === page
                        ? `bg-${PRIMARY_COLOR} text-white`
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : `bg-${PRIMARY_COLOR} text-white hover:bg-blue-700`
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup Modal */}
      {popup.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <DepartmentPopup
              mode={popup.mode}
              department={popup.department}
              onClose={closePopup}
              onSuccess={handleSuccess}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default DepartmentComponent
